import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import './AccountSettingPage.css';

function AccountSettingPage({ accountId: propAccountId }) {
  const storedAccountId = localStorage.getItem('accountId');
  const storedLinkedinAccountId = localStorage.getItem('linkedin_account_id');
  const accountId = propAccountId || storedAccountId || storedLinkedinAccountId || null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  // Working hours settings
  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [workingDays, setWorkingDays] = useState([1, 2, 3, 4, 5]);
  const [workingHoursStart, setWorkingHoursStart] = useState('09:00');
  const [workingHoursEnd, setWorkingHoursEnd] = useState('17:00');

  // Daily limits settings
  const [limitSettings, setLimitSettings] = useState({
    max_connection_requests: 30,
    max_messages: 50,
    max_inmails: 40,
    max_profile_views: 40,
    max_follows: 40,
    max_post_likes: 40,
    max_weekly_connection_requests: 100
  });

  const [withdrawSettings, setWithdrawSettings] = useState({
    max_pending_invitations: 500,
    withdraw_buffer: 50,
    max_withdrawals_per_day: 20,
    withdraw_after_days: 30
  });

  const loadSettings = useCallback(async () => {
    if (!accountId) return;

    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/workflow/settings/${accountId}`);
      if (response.data.success && response.data.settings) {
        const s = response.data.settings;

        // Working hours
        setTimezone(s.timezone || 'America/Los_Angeles');
        const days = s.working_days
          ? (typeof s.working_days === 'string' ? s.working_days.split(',').map(Number) : s.working_days)
          : [1, 2, 3, 4, 5];
        setWorkingDays(days);
        setWorkingHoursStart(s.working_hours_start || '09:00');
        setWorkingHoursEnd(s.working_hours_end || '17:00');

        // Daily limits
        setLimitSettings({
          max_connection_requests: s.max_connection_requests ?? 30,
          max_messages: s.max_messages ?? 50,
          max_inmails: s.max_inmails ?? 40,
          max_profile_views: s.max_profile_views ?? 40,
          max_follows: s.max_follows ?? 40,
          max_post_likes: s.max_post_likes ?? 40,
          max_weekly_connection_requests: s.max_weekly_connection_requests ?? 100
        });
        setWithdrawSettings({
          max_pending_invitations: s.max_pending_invitations ?? 500,
          withdraw_buffer: s.withdraw_buffer ?? 50,
          max_withdrawals_per_day: s.max_withdrawals_per_day ?? 20,
          withdraw_after_days: s.withdraw_after_days ?? 30
        });
      }
    } catch (error) {
      console.error('Load settings error:', error);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    if (!accountId) return;

    setSaving(true);
    setSuccessMsg('');
    try {
      const payload = {
        ...limitSettings,
        ...withdrawSettings,
        timezone,
        working_days: workingDays.join(','),
        working_hours_start: workingHoursStart,
        working_hours_end: workingHoursEnd
      };

      const response = await axios.put(`${API_BASE_URL}/workflow/settings/${accountId}`, payload);
      if (response.data.success) {
        setSuccessMsg('Settings saved successfully!');
        setTimeout(() => setSuccessMsg(''), 3000);
      }
    } catch (error) {
      console.error('Save settings error:', error);
      alert('Failed to save settings: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    loadSettings();
  };

  if (!accountId) {
    return (
      <div className="account-settings-page">
        <div className="error-state">
          <h3>No account connected</h3>
          <p>Please login with your LinkedIn account first.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="account-settings-page">
        <div className="loading-state">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="account-settings-page">
      <div className="page-header">
        <div>
          <h2>Account Settings</h2>
          <div className="account-id-display">Account: {accountId}</div>
        </div>
      </div>

      {successMsg && (
        <div className="success-message">
          ✅ {successMsg}
        </div>
      )}

      {/* Working Hours Section */}
      <div className="settings-card">
        <h3>Working Hours</h3>

        <div className="form-group">
          <label>Timezone</label>
          <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            <option value="America/Los_Angeles">Pacific Time (PT)</option>
            <option value="America/Denver">Mountain Time (MT)</option>
            <option value="America/Chicago">Central Time (CT)</option>
            <option value="America/New_York">Eastern Time (ET)</option>
            <option value="Europe/London">London (GMT)</option>
            <option value="Europe/Paris">Paris (CET)</option>
            <option value="Asia/Shanghai">Shanghai (CST)</option>
            <option value="Asia/Tokyo">Tokyo (JST)</option>
          </select>
        </div>

        <div className="form-group">
          <label>Working Days</label>
          <div className="days-selector">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
              <label key={index} className="day-checkbox">
                <input
                  type="checkbox"
                  checked={workingDays.includes(index)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setWorkingDays([...workingDays, index].sort());
                    } else {
                      setWorkingDays(workingDays.filter(d => d !== index));
                    }
                  }}
                />
                <span>{day}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Working Hours Start</label>
            <input
              type="time"
              value={workingHoursStart}
              onChange={(e) => setWorkingHoursStart(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Working Hours End</label>
            <input
              type="time"
              value={workingHoursEnd}
              onChange={(e) => setWorkingHoursEnd(e.target.value)}
            />
          </div>
        </div>

        <div className="info-box">
          <p>All campaigns under this account will follow these working hours. Tasks will only be scheduled and executed during the configured working hours and days.</p>
        </div>
      </div>

      {/* Daily Limits Section */}
      <div className="settings-card">
        <h3>Daily Action Limits</h3>

        <div className="limits-grid">
          <div className="limit-item">
            <label>Connection Requests / Day</label>
            <input
              type="number"
              min="0"
              max="100"
              value={limitSettings.max_connection_requests}
              onChange={(e) => setLimitSettings({ ...limitSettings, max_connection_requests: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div className="limit-item">
            <label>Connection Requests / Week</label>
            <input
              type="number"
              min="0"
              max="500"
              value={limitSettings.max_weekly_connection_requests}
              onChange={(e) => setLimitSettings({ ...limitSettings, max_weekly_connection_requests: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div className="limit-item">
            <label>Messages / Day</label>
            <input
              type="number"
              min="0"
              max="100"
              value={limitSettings.max_messages}
              onChange={(e) => setLimitSettings({ ...limitSettings, max_messages: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div className="limit-item">
            <label>InMails / Day</label>
            <input
              type="number"
              min="0"
              max="100"
              value={limitSettings.max_inmails}
              onChange={(e) => setLimitSettings({ ...limitSettings, max_inmails: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div className="limit-item">
            <label>Profile Views / Day</label>
            <input
              type="number"
              min="0"
              max="100"
              value={limitSettings.max_profile_views}
              onChange={(e) => setLimitSettings({ ...limitSettings, max_profile_views: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div className="limit-item">
            <label>Follows / Day</label>
            <input
              type="number"
              min="0"
              max="100"
              value={limitSettings.max_follows}
              onChange={(e) => setLimitSettings({ ...limitSettings, max_follows: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div className="limit-item">
            <label>Post Likes / Day</label>
            <input
              type="number"
              min="0"
              max="100"
              value={limitSettings.max_post_likes}
              onChange={(e) => setLimitSettings({ ...limitSettings, max_post_likes: parseInt(e.target.value) || 0 })}
            />
          </div>
        </div>

        <div className="info-box">
          <p><strong>LinkedIn Recommended Limits:</strong></p>
          <ul>
            <li>Connection requests: 30/day</li>
            <li>Messages: 50/day</li>
            <li>InMails: 40/day</li>
            <li>Profile views: 40/day</li>
          </ul>
        </div>
      </div>

      <div className="settings-card">
        <h3>Pending Invitation Cleanup</h3>

        <div className="limits-grid">
          <div className="limit-item">
            <label>Maximum Pending Invitations</label>
            <input
              type="number"
              min="1"
              max="1000"
              value={withdrawSettings.max_pending_invitations}
              onChange={(e) => setWithdrawSettings({ ...withdrawSettings, max_pending_invitations: parseInt(e.target.value) || 1 })}
            />
          </div>
          <div className="limit-item">
            <label>Withdraw Buffer</label>
            <input
              type="number"
              min="0"
              max="500"
              value={withdrawSettings.withdraw_buffer}
              onChange={(e) => setWithdrawSettings({ ...withdrawSettings, withdraw_buffer: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div className="limit-item">
            <label>Maximum Withdrawals / Day</label>
            <input
              type="number"
              min="0"
              max="100"
              value={withdrawSettings.max_withdrawals_per_day}
              onChange={(e) => setWithdrawSettings({ ...withdrawSettings, max_withdrawals_per_day: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div className="limit-item">
            <label>Withdraw After Pending Days</label>
            <input
              type="number"
              min="1"
              max="365"
              value={withdrawSettings.withdraw_after_days}
              onChange={(e) => setWithdrawSettings({ ...withdrawSettings, withdraw_after_days: parseInt(e.target.value) || 1 })}
            />
          </div>
        </div>

        <div className="info-box">
          <p>Old pending invitations are withdrawn first. A withdrawn recipient cannot be invited again by this account until the 21-day cooldown ends.</p>
        </div>
      </div>

      {/* Save / Reset */}
      <div className="form-actions">
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        <button className="btn-secondary" onClick={handleReset}>
          Reset
        </button>
      </div>
    </div>
  );
}

export default AccountSettingPage;
